import React, {Fragment, useEffect, useMemo, useState} from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid'
import {getColor} from 'components/utils'
import {TransProps} from '@lingui/react'
import {classNames} from 'components/utils'
import { Trans } from '@lingui/macro';


// Types
export type typeValuesListItem = {
    id: string | number,
    name: string | React.ReactElement<TransProps>,
    deleted?: boolean
}

export type typeSelectBox = {
    id?: string
    variant?: string
    placeholder?: string
    label?: React.ReactElement<TransProps>
    valuesList: typeValuesListItem[]
    valueId: string | number | null
    setIdValue: React.Dispatch<React.SetStateAction<any>>
    labelClassname?: string
    selectBosClassname?: string
    required?: boolean
    disableLabel?: boolean
    withoutEmpty?: boolean
    errorMessage?: React.ReactNode;
}

export const NULL_VALUE = {id: '#null#', name: '-'}

// Component
export const SelectBox: React.FC<typeSelectBox> = ({
    id,
    variant,
    label,
    valuesList,
    valueId,
    setIdValue,
    labelClassname,
    selectBosClassname,
    required=false,
    withoutEmpty=false,
    errorMessage,
    disableLabel,
    placeholder
}) => {

    const color = getColor(variant)

    const labelStyles = ['block mb-1 text-sm font-medium text-gray-700', labelClassname].join(' ')
    const selectBoxStyles = ['relative', selectBosClassname].join(' ')

    //let newValuesList = [...[NULL_VALUE], ...valuesList]
    let newEmptyValuesList = useMemo(() => [...[NULL_VALUE], ...valuesList], [valuesList])
    let newWithoutEmptyValuesList = useMemo(() => valuesList.length ? valuesList : [...[NULL_VALUE], ...valuesList], [valuesList])


    let newValuesList = withoutEmpty ? newWithoutEmptyValuesList : newEmptyValuesList

    const [selected, setSelected] = useState(newValuesList[0])

    useEffect(() => {
        const selectedItemIndex = newValuesList.findIndex((item) => item.id === valueId)
        if(selectedItemIndex >= 0) setSelected(newValuesList[selectedItemIndex])
        // eslint-disable-next-line
    },[valueId, valuesList])

    useEffect(() => {
        if (selected.id === NULL_VALUE.id) {
            setIdValue(null)
        } else {
            setIdValue(selected.id)
        }
        // eslint-disable-next-line
    },[selected, setIdValue])

    return (
        <Listbox value={selected} onChange={setSelected}>
            {({ open }) => (
                <>
                    { !disableLabel && (
                      <Listbox.Label
                        className={ labelStyles }
                      >{ required && <span className="text-red-600 mr-1">*</span> }{ label }
                      </Listbox.Label>
                    ) }
                    <div id={id} className={selectBoxStyles}>
                        <Listbox.Button className={`relative w-full bg-white border ${errorMessage? 'border-red-500 ': 'border-gray-300' } rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-${color}-500 focus:border-${color}-500 sm:text-sm`}>
                            {selected.name === NULL_VALUE.name && placeholder ? (
                              <span
                                className="block truncate"
                                style={ { color: '#9da4b0' } }
                              >{ placeholder }
                              </span>
                              ) : (
                              <span className="block truncate">{ selected.name }</span>
                            ) }
                            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </span>
                        </Listbox.Button>

                        <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                {newValuesList.map((optionItem) => (
                                    <Listbox.Option
                                        key={optionItem.id}
                                        className={({ active }) =>
                                            classNames(
                                                active ? `text-white bg-${color}-600` : 'text-gray-900',
                                                'cursor-default select-none relative py-2 pl-8 pr-4'
                                            )
                                        }
                                        value={optionItem}
                                    >
                                        {({ selected, active }) => (
                                            <>
                                                <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate text-left')}>

                                                    {optionItem.name}{
                                                    // @ts-ignore
                                                        optionItem.deleted  ? <span
                                                    className="text-xs text-gray-400 font-normal"><Trans>(deleted)</Trans></span> : null}
                                                </span>

                                                {selected ? (
                                                    <span
                                                        className={classNames(
                                                            active ? 'text-white' : `text-${color}-600`,
                                                            'absolute inset-y-0 left-0 flex items-center pl-1.5'
                                                        )}
                                                    >
                                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                ) : null}
                                            </>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                    { errorMessage
                        ? <p className="text-xs mt-1 text-red-500">{ errorMessage }</p>
                        : null
                    }
                </>
            )}
        </Listbox>
    )
}
